import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Icon } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone, TableWrap } from '../components/ui.tsx';
import { currentLang, t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day as dayText, money, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi } from './Reports.tsx';

/**
 * Appointments: the services the business offers, the people who do them and their hours, a day
 * calendar with a column per person, bookings by phone at the desk, and the public booking page
 * where customers pick a free time themselves.
 */

type State = 'booked' | 'done' | 'no_show' | 'cancelled';
type ToneName = 'blue' | 'green' | 'orange' | 'purple' | 'red' | 'teal' | 'yellow' | 'grey';
type When = 'upcoming' | 'to_close' | 'past' | 'cancelled' | 'all';
type Tab = 'day' | 'list' | 'services' | 'team';

interface Row {
  id: string;
  ref: string;
  service: string;
  service_name: string;
  service_tone: ToneName;
  staff: string;
  staff_name: string;
  starts: string;
  ends: string;
  day: string;
  time: string;
  end_time: string;
  starts_local: string;
  name: string;
  email: string | null;
  phone: string | null;
  note: string | null;
  state: State;
  source: 'web' | 'staff';
  price: number;
  cancel_reason: string | null;
  cancelled_at: string | null;
  created_at: string;
  token: string;
}
interface Settings {
  public: boolean;
  intro: string | null;
  step: number;
  notice_hours: number;
  days_ahead: number;
  cancel_hours: number;
}
interface Overview {
  settings: Settings;
  today: string;
  now: string;
  counts: { today: number; week: number; week_value: number; online_30: number; booked_30: number; no_show_30: number; seen_30: number; to_close: number };
  today_list: Row[];
  next: Row[];
  ready: { services: number; staff: number; hours: number };
  can: { book: boolean; manage: boolean };
}
interface DayStaff {
  id: string;
  name: string;
  tone: ToneName;
  services: string[];
  hours: { starts: string; ends: string }[];
  off: { id: string; from: number; to: number; reason: string | null }[];
}
interface DayView {
  day: string;
  today: string;
  now: string;
  staff: DayStaff[];
  appointments: (Row & { from: number; to: number; busy_to: number })[];
  cancelled: number;
  services: { id: string; name: string; duration: number; buffer: number; price: number; tone: ToneName }[];
  can: { book: boolean; manage: boolean };
}
interface Service {
  id: string;
  name: string;
  description: string | null;
  duration: number;
  buffer: number;
  price: number;
  active: boolean;
  online: boolean;
  tone: ToneName;
  staff: { id: string; name: string }[];
  booked: number;
}
interface Person {
  id: string;
  name: string;
  user: string | null;
  active: boolean;
  services: string[];
  tone: ToneName;
  hours: { weekday: number; starts: string; ends: string }[];
  off: { id: string; starts_local: string; ends_local: string; reason: string | null }[];
  upcoming: number;
}
interface Team {
  staff: Person[];
  users: { id: string; name: string }[];
  services: { id: string; name: string }[];
}
interface Slot {
  time: string;
  at: string;
  staff: string[];
}
type Detail = Row & { history: { done: number; no_show: number }; can: { book: boolean; manage: boolean } };

const TONES: ToneName[] = ['blue', 'green', 'orange', 'purple', 'red', 'teal', 'yellow', 'grey'];
const STATE_TONE: Record<State, Tone> = { booked: 'info', done: 'ok', no_show: 'warn', cancelled: 'neutral' };
const STATE_ICON = { booked: 'calendar', done: 'checkCircle', no_show: 'alert', cancelled: 'x' } as const;
const publicUrl = (tenant: string) => `${window.location.origin}/book/${tenant}`;
const plusDays = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const weekdayOf = (d: string) => {
  const w = new Date(`${d}T12:00:00Z`).getUTCDay();
  return w === 0 ? 7 : w;
};
const longDay = (d: string) => new Intl.DateTimeFormat(currentLang(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`));
const wdName = (w: number, style: 'short' | 'long' = 'long') => new Intl.DateTimeFormat(currentLang(), { weekday: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, w)));
const minutesText = (n: number) => (n < 60 ? t('ap.min', { n }) : n % 60 ? t('ap.h_min', { h: Math.floor(n / 60), m: n % 60 }) : tn('ap.hours', n / 60));
const mins = (clock: string) => {
  const [h, m] = clock.split(':').map(Number) as [number, number];
  return h * 60 + m;
};
const clockOf = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const initials = (name: string) =>
  name
    .replace(/\(.*\)/, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

function StateBadge({ state }: { state: State }) {
  return <StatusBadge tone={STATE_TONE[state]} icon={STATE_ICON[state]} label={t(`ap.st_${state}` as MessageKey)} />;
}

export function Appointments() {
  const [params, setParams] = useSearchParams();
  const { data: o, reload } = useAction<Overview>('appointments.overview');
  const { tenant } = useSession();
  const slug = tenant?.slug ?? '';
  const tab = (['list', 'services', 'team'].includes(params.get('tab') ?? '') ? params.get('tab') : 'day') as Tab;
  const [settings, setSettings] = useState(false);
  const [booking, setBooking] = useState<{ staff?: string | null; day?: string; time?: string } | null>(null);
  const [open, setOpen] = useState<string | null>(params.get('a'));
  const [version, setVersion] = useState(0);
  const set = (patch: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p);
  };
  const changed = () => {
    setVersion((v) => v + 1);
    void reload();
  };
  const day = params.get('day') ?? o?.today ?? null;
  return (
    <Shell>
      <div className="page reports ap-page">
        <div className="page-head">
          <div>
            <h1>{t('ap.title')}</h1>
            <p className="muted">{t('ap.subtitle')}</p>
          </div>
          <div className="page-actions">
            {o?.settings.public ? (
              <a className="btn btn-ghost btn-md" href={publicUrl(slug)} target="_blank" rel="noopener noreferrer">
                <Icon name="external" size={20} />
                <span className="btn-label">{t('ap.public_page')}</span>
              </a>
            ) : null}
            {o?.can.manage ? <IconButton icon="settings" label={t('ap.settings')} onClick={() => setSettings(true)} /> : null}
            {o?.can.book ? (
              <Button variant="primary" icon="plus" onClick={() => setBooking({ day: day ?? undefined })} disabled={!o.ready.services || !o.ready.staff}>
                {t('ap.new')}
              </Button>
            ) : null}
          </div>
        </div>
        <div className="segmented tabs" role="tablist" aria-label={t('ap.sections')}>
          {(['day', 'list', 'services', 'team'] as const).map((k) => (
            <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => set({ tab: k === 'day' ? null : k })}>
              <Icon name={k === 'day' ? 'calendar' : k === 'list' ? 'list' : k === 'services' ? 'sparkle' : 'users'} size={16} />
              {t(`ap.tab_${k}` as MessageKey)}
              {k === 'list' && o?.counts.to_close ? <span className="count">{o.counts.to_close}</span> : null}
            </button>
          ))}
        </div>
        {!o || !day ? (
          <Skeleton h={420} r="var(--radius-lg)" />
        ) : tab === 'day' ? (
          <DayTab
            o={o}
            day={day}
            version={version}
            setDay={(d) => set({ day: d === o.today ? null : d })}
            onBook={(p) => setBooking(p)}
            onOpen={setOpen}
            onSetup={(to) => (to === 'settings' ? setSettings(true) : set({ tab: to }))}
            onToClose={() => set({ tab: 'list', when: 'to_close' })}
          />
        ) : tab === 'list' ? (
          <ListTab when={(params.get('when') as When | null) ?? 'upcoming'} setWhen={(w) => set({ when: w === 'upcoming' ? null : w })} version={version} onOpen={setOpen} />
        ) : tab === 'services' ? (
          <ServicesTab can={o.can} onChange={changed} />
        ) : (
          <TeamTab can={o.can} onChange={changed} />
        )}
      </div>
      {settings && o ? (
        <SettingsDialog
          initial={o.settings}
          slug={slug}
          onClose={() => setSettings(false)}
          onSaved={() => {
            setSettings(false);
            changed();
          }}
        />
      ) : null}
      {booking ? (
        <BookDialog
          initial={booking}
          today={o?.today ?? ''}
          onClose={() => setBooking(null)}
          onDone={(id) => {
            setBooking(null);
            changed();
            setOpen(id);
          }}
        />
      ) : null}
      {open ? <ApptDialog key={open} id={open} today={o?.today ?? ''} slug={slug} onClose={() => setOpen(null)} onChange={changed} /> : null}
    </Shell>
  );
}

// ---- the day ------------------------------------------------------------------------------

const PX = 1.2; // pixels per minute: an hour is 72px high

function DayTab({
  o,
  day,
  version,
  setDay,
  onBook,
  onOpen,
  onSetup,
  onToClose,
}: {
  o: Overview;
  day: string;
  version: number;
  setDay: (d: string) => void;
  onBook: (p: { staff?: string | null; day?: string; time?: string }) => void;
  onOpen: (id: string) => void;
  onSetup: (to: 'services' | 'team' | 'settings') => void;
  onToClose: () => void;
}) {
  const { data: v, reload } = useAction<DayView>('appointments.day', { day });
  useEffect(() => {
    if (version) void reload();
  }, [version, reload]);
  const c = o.counts;
  const noShow = c.seen_30 ? Math.round((c.no_show_30 / c.seen_30) * 100) : null;
  const ready = o.ready.services && o.ready.staff && o.ready.hours;
  return (
    <div className="ap-day-tab">
      {!ready && o.can.manage ? (
        <section className="panel ap-setup" aria-label={t('ap.setup_h')}>
          <h2>{t('ap.setup_h')}</h2>
          <ol className="ap-setup-steps">
            <li className={o.ready.services ? 'done' : ''}>
              <Icon name={o.ready.services ? 'checkCircle' : 'circle'} size={18} />
              <span>{t('ap.setup_services')}</span>
              {!o.ready.services ? (
                <Button size="sm" onClick={() => onSetup('services')}>
                  {t('ap.add_service')}
                </Button>
              ) : null}
            </li>
            <li className={o.ready.staff && o.ready.hours ? 'done' : ''}>
              <Icon name={o.ready.staff && o.ready.hours ? 'checkCircle' : 'circle'} size={18} />
              <span>{t('ap.setup_team')}</span>
              {!(o.ready.staff && o.ready.hours) ? (
                <Button size="sm" onClick={() => onSetup('team')}>
                  {t('ap.add_person')}
                </Button>
              ) : null}
            </li>
            <li className={o.settings.public ? 'done' : ''}>
              <Icon name={o.settings.public ? 'checkCircle' : 'circle'} size={18} />
              <span>{t('ap.setup_page')}</span>
              {!o.settings.public ? (
                <Button size="sm" onClick={() => onSetup('settings')}>
                  {t('ap.open_page')}
                </Button>
              ) : null}
            </li>
          </ol>
        </section>
      ) : null}
      <section className="rep-kpis" aria-label={t('ap.glance')}>
        <Kpi label={t('ap.k_today')} value={String(c.today)} hint={o.next[0] && o.next[0].day === o.today ? t('ap.k_next', { time: o.next[0].time }) : undefined} />
        <Kpi label={t('ap.k_week')} value={String(c.week)} hint={c.week_value ? t('ap.k_value', { amount: money(c.week_value) }) : undefined} />
        <Kpi label={t('ap.k_online')} value={c.booked_30 ? `${Math.round((c.online_30 / c.booked_30) * 100)}%` : '—'} hint={tn('ap.k_booked_30', c.booked_30)} />
        <Kpi label={t('ap.k_no_show')} value={noShow === null ? '—' : `${noShow}%`} hint={c.to_close ? tn('ap.k_to_close', c.to_close) : t('ap.k_all_closed')} warn={c.to_close > 0} />
      </section>
      {c.to_close ? (
        <p className="callout callout-warn ap-callout">
          <Icon name="clock" size={18} />
          <span>{tn('ap.to_close_note', c.to_close)}</span>
          <Button size="sm" onClick={onToClose}>
            {t('ap.see_them')}
          </Button>
        </p>
      ) : null}
      <div className="ap-daybar">
        <div className="ap-daynav">
          <IconButton icon="back" label={t('ap.prev_day')} onClick={() => setDay(plusDays(day, -1))} />
          <IconButton icon="chevronRight" label={t('ap.next_day')} onClick={() => setDay(plusDays(day, 1))} />
          <Button size="sm" variant="ghost" onClick={() => setDay(o.today)} disabled={day === o.today}>
            {t('ap.today')}
          </Button>
          <input type="date" className="input ap-date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} aria-label={t('ap.pick_day')} />
        </div>
        <h2 className="ap-dayname">
          {longDay(day)}
          {v ? <span className="muted small"> · {tn('ap.n_appts', v.appointments.length)}</span> : null}
        </h2>
      </div>
      {!v ? (
        <Skeleton h={420} r="var(--radius-lg)" />
      ) : !v.staff.length ? (
        <EmptyState icon="users" title={t('ap.no_team')} body={t('ap.no_team_body')} />
      ) : (
        <>
          <DayGrid v={v} onBook={onBook} onOpen={onOpen} />
          <Agenda v={v} onOpen={onOpen} onBook={onBook} />
          {v.cancelled ? <p className="muted small ap-cancelled-note">{tn('ap.n_cancelled_day', v.cancelled)}</p> : null}
        </>
      )}
    </div>
  );
}

function DayGrid({ v, onBook, onOpen }: { v: DayView; onBook: (p: { staff?: string | null; day?: string; time?: string }) => void; onOpen: (id: string) => void }) {
  const scroller = useRef<HTMLDivElement>(null);
  const all = v.staff.flatMap((s) => s.hours.flatMap((h) => [mins(h.starts), mins(h.ends)]));
  const booked = v.appointments.flatMap((a) => [a.from, a.busy_to]);
  const first = Math.max(0, Math.floor((Math.min(8 * 60, ...all, ...booked) - 30) / 60) * 60);
  const last = Math.min(24 * 60, Math.ceil((Math.max(18 * 60, ...all, ...booked) + 30) / 60) * 60);
  const hours = Array.from({ length: (last - first) / 60 }, (_, i) => first + i * 60);
  const isToday = v.day === v.today;
  const nowMin = isToday ? Math.round((Date.parse(v.now) - Date.parse(`${v.day}T00:00:00`)) / 60_000) : null;
  const [now, setNow] = useState(nowMin);
  useEffect(() => {
    if (!isToday) return;
    const start = Date.now();
    const id = setInterval(() => setNow((nowMin ?? 0) + Math.round((Date.now() - start) / 60_000)), 60_000);
    return () => clearInterval(id);
  }, [isToday, nowMin]);
  useEffect(() => {
    // Open on the working day, or around now.
    const target = now !== null && now > first ? now - 90 : Math.min(...all, last) - 30;
    scroller.current?.scrollTo({ top: Math.max(0, (target - first) * PX) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.day]);
  const height = (last - first) * PX;
  const y = (m: number) => (m - first) * PX;
  const click = (s: DayStaff, e: React.MouseEvent<HTMLDivElement>) => {
    if (!v.can.book) return;
    const r = e.currentTarget.getBoundingClientRect();
    const m = Math.floor((first + (e.clientY - r.top) / PX) / 15) * 15;
    onBook({ staff: s.id, day: v.day, time: clockOf(Math.max(0, Math.min(m, 24 * 60 - 15))) });
  };
  return (
    <div className="panel ap-grid-panel">
      {/* Scrolls on its own (long days, many staff): keyboard users reach it with Tab and scroll with the arrow keys. */}
      <div className="ap-grid-scroll" ref={scroller} role="region" tabIndex={0} aria-label={t('ap.day_grid')}>
        <div className="ap-grid" style={{ gridTemplateColumns: `56px repeat(${v.staff.length}, minmax(150px, 1fr))` }}>
          <div className="ap-corner" />
          {v.staff.map((s) => (
            <div key={s.id} className="ap-col-head">
              <span className={`ap-avatar tone-${s.tone}`} aria-hidden="true">
                {initials(s.name)}
              </span>
              <span className="ap-col-name">
                <strong>{s.name}</strong>
                <span className="muted small">{s.hours.length ? s.hours.map((h) => `${h.starts}–${h.ends}`).join(', ') : t('ap.day_off')}</span>
              </span>
            </div>
          ))}
          <div className="ap-hours" style={{ height }}>
            {hours.map((h) => (
              <span key={h} className="ap-hour" style={{ top: y(h) }}>
                {clockOf(h)}
              </span>
            ))}
          </div>
          {v.staff.map((s) => (
            <div
              key={s.id}
              className={`ap-col${v.can.book ? ' bookable' : ''}`}
              style={{ height }}
              onClick={(e) => click(s, e)}
              role="presentation"
              title={v.can.book ? t('ap.click_to_book') : undefined}
            >
              {hours.map((h) => (
                <span key={h} className="ap-line" style={{ top: y(h) }} />
              ))}
              {/* Outside the working hours: shaded. */}
              {(() => {
                const spans = [...s.hours].map((h) => [mins(h.starts), mins(h.ends)] as const).sort((a, b) => a[0] - b[0]);
                const out: [number, number][] = [];
                let at = first;
                for (const [a, b] of spans) {
                  if (a > at) out.push([at, a]);
                  at = Math.max(at, b);
                }
                if (at < last) out.push([at, last]);
                return out.map(([a, b]) => <span key={`c${a}`} className="ap-closed" style={{ top: y(a), height: (b - a) * PX }} />);
              })()}
              {s.off.map((x) => (
                <span key={x.id} className="ap-off" style={{ top: y(Math.max(x.from, first)), height: (Math.min(x.to, last) - Math.max(x.from, first)) * PX }}>
                  <Icon name="moon" size={14} /> {x.reason ?? t('ap.time_off')}
                </span>
              ))}
              {v.appointments
                .filter((a) => a.staff === s.id)
                .map((a) => {
                  const h = Math.max((a.to - a.from) * PX, 26);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className={`ap-appt tone-${a.service_tone} st-${a.state}${h < 44 ? ' short' : ''}`}
                      style={{ top: y(a.from), height: h }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen(a.id);
                      }}
                      aria-label={`${a.time}–${a.end_time}, ${a.name}, ${a.service_name}, ${t(`ap.st_${a.state}` as MessageKey)}`}
                    >
                      <span className="ap-appt-top">
                        <strong>{a.name}</strong>
                        {a.state === 'done' ? <Icon name="check" size={14} /> : a.state === 'no_show' ? <Icon name="alert" size={14} /> : a.source === 'web' ? <Icon name="globe" size={14} /> : null}
                      </span>
                      <span className="ap-appt-sub">
                        {a.time}–{a.end_time} · {a.service_name}
                      </span>
                      {a.busy_to > a.to ? <span className="ap-buffer" style={{ height: (a.busy_to - a.to) * PX }} aria-hidden="true" /> : null}
                    </button>
                  );
                })}
              {now !== null && now >= first && now <= last ? <span className="ap-now" style={{ top: y(now) }} aria-hidden="true" /> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** On a phone: the day as a list, person by person. */
function Agenda({ v, onOpen, onBook }: { v: DayView; onOpen: (id: string) => void; onBook: (p: { staff?: string | null; day?: string }) => void }) {
  return (
    <div className="ap-agenda">
      {v.staff.map((s) => {
        const list = v.appointments.filter((a) => a.staff === s.id);
        return (
          <section key={s.id} className="panel ap-agenda-person">
            <header className="ap-agenda-head">
              <span className={`ap-avatar tone-${s.tone}`} aria-hidden="true">
                {initials(s.name)}
              </span>
              <span className="ap-col-name">
                <strong>{s.name}</strong>
                <span className="muted small">{s.hours.length ? s.hours.map((h) => `${h.starts}–${h.ends}`).join(', ') : t('ap.day_off')}</span>
              </span>
              {v.can.book && s.hours.length ? <IconButton icon="plus" label={t('ap.book_with', { name: s.name })} onClick={() => onBook({ staff: s.id, day: v.day })} /> : null}
            </header>
            {s.off.length ? (
              <p className="muted small">
                <Icon name="moon" size={14} /> {s.off.map((x) => x.reason ?? t('ap.time_off')).join(', ')}
              </p>
            ) : null}
            {list.length ? (
              <ul className="ap-agenda-list">
                {list.map((a) => (
                  <li key={a.id}>
                    <button type="button" className={`ap-agenda-row st-${a.state}`} onClick={() => onOpen(a.id)}>
                      <span className={`ap-agenda-bar tone-${a.service_tone}`} aria-hidden="true" />
                      <span className="ap-agenda-time mono">{a.time}</span>
                      <span className="ap-agenda-main">
                        <strong>{a.name}</strong>
                        <span className="muted small">{a.service_name}</span>
                      </span>
                      <StateBadge state={a.state} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted small">{t('ap.nothing_booked')}</p>
            )}
          </section>
        );
      })}
    </div>
  );
}

// ---- the list -----------------------------------------------------------------------------

function ListTab({ when, setWhen, version, onOpen }: { when: When; setWhen: (w: When) => void; version: number; onOpen: (id: string) => void }) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const { data: rows, reload } = useAction<Row[]>('appointments.list', { when, q: query || null });
  useEffect(() => {
    if (version) void reload();
  }, [version, reload]);
  const csv = () =>
    rows &&
    downloadCsv(
      `appointments-${when}.csv`,
      [t('ap.c_ref'), t('ap.c_day'), t('ap.c_time'), t('ap.c_customer'), t('ap.c_phone'), t('ap.c_email'), t('ap.c_service'), t('ap.c_with'), t('ap.c_price'), t('ap.c_source'), t('ap.c_state')],
      rows.map((r) => [
        r.ref,
        r.day,
        r.time,
        r.name,
        r.phone ?? '',
        r.email ?? '',
        r.service_name,
        r.staff_name,
        money(r.price),
        t(`ap.src_${r.source}` as MessageKey),
        t(`ap.st_${r.state}` as MessageKey),
      ]),
    );
  return (
    <section className="panel ap-list">
      <div className="ev-filters">
        <div className="segmented subtle" role="tablist" aria-label={t('ap.show')}>
          {(['upcoming', 'to_close', 'past', 'cancelled', 'all'] as const).map((w) => (
            <button key={w} role="tab" aria-selected={when === w} className={when === w ? 'active' : ''} onClick={() => setWhen(w)}>
              {t(`ap.w_${w}` as MessageKey)}
            </button>
          ))}
        </div>
        <div className="ev-filter-end">
          <input className="input ev-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ap.search')} aria-label={t('ap.search')} />
          <IconButton icon="download" label={t('ap.export')} onClick={csv} disabled={!rows?.length} />
        </div>
      </div>
      {!rows ? (
        <Skeleton h={240} />
      ) : !rows.length ? (
        <EmptyState icon="calendar" title={query ? t('ap.no_match') : t(`ap.empty_${when}` as MessageKey)} body={query ? t('ap.no_match_body') : undefined} />
      ) : (
        <TableWrap>
          <table className="data-table ap-table">
            <thead>
              <tr>
                <th>{t('ap.c_when')}</th>
                <th className="left">{t('ap.c_customer')}</th>
                <th className="left">{t('ap.c_service')}</th>
                <th className="left">{t('ap.c_with')}</th>
                <th>{t('ap.c_price')}</th>
                <th className="left">{t('ap.c_state')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="clickable" onClick={() => onOpen(r.id)}>
                  <td>
                    <button type="button" className="row-link" onClick={() => onOpen(r.id)}>
                      <strong>{dayText(r.day)}</strong>
                      <span className="muted small">
                        {' '}
                        {r.time}–{r.end_time}
                      </span>
                    </button>
                  </td>
                  <td className="left">
                    <span className="ap-cust">
                      {r.name}
                      {r.source === 'web' ? (
                        <span className="ap-web" title={t('ap.src_web')}>
                          <Icon name="globe" size={14} />
                        </span>
                      ) : null}
                    </span>
                    <span className="muted small">{r.phone ?? r.email ?? ''}</span>
                  </td>
                  <td className="left">
                    <span className={`ap-dot tone-${r.service_tone}`} aria-hidden="true" /> {r.service_name}
                  </td>
                  <td className="left">{r.staff_name}</td>
                  <td>{r.price ? money(r.price) : t('ap.free')}</td>
                  <td className="left">
                    <StateBadge state={r.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </section>
  );
}

// ---- one appointment ----------------------------------------------------------------------

function ApptDialog({ id, today, slug, onClose, onChange }: { id: string; today: string; slug: string; onClose: () => void; onChange: () => void }) {
  const { data: a, reload } = useAction<Detail>('appointments.get', { id });
  const { toast } = useUi();
  const { busy, run } = useSave();
  const [mode, setMode] = useState<'view' | 'edit' | 'move'>('view');
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const done = () => {
    onChange();
    void reload();
  };
  const setState = (state: State) => void run(() => action('appointments.set_state', { id, state }).then(done), t(`ap.saved_${state}` as MessageKey));
  if (!a) {
    return (
      <Dialog title={t('ap.appointment')} onClose={onClose}>
        <Skeleton h={200} />
      </Dialog>
    );
  }
  const started = Date.parse(a.starts) <= Date.now();
  const can = a.can.book;
  return (
    <Dialog
      title={`${a.service_name} · ${a.ref}`}
      onClose={onClose}
      footer={
        mode !== 'view' ? null : (
          <>
            {can && a.state === 'booked' ? (
              <Button variant="ghost" icon="x" className="ap-danger-text" onClick={() => setCancelling(true)}>
                {t('ap.cancel_appt')}
              </Button>
            ) : null}
            <span className="ap-foot-gap" />
            {can && a.state === 'booked' && !started ? (
              <Button icon="move" onClick={() => setMode('move')}>
                {t('ap.move')}
              </Button>
            ) : null}
            {can && a.state === 'booked' && started ? (
              <>
                <Button icon="alert" busy={busy} onClick={() => setState('no_show')}>
                  {t('ap.no_show')}
                </Button>
                <Button variant="primary" icon="check" busy={busy} onClick={() => setState('done')}>
                  {t('ap.mark_done')}
                </Button>
              </>
            ) : null}
            {can && (a.state === 'done' || a.state === 'no_show') ? (
              <Button icon="undo" busy={busy} onClick={() => setState('booked')}>
                {t('ap.reopen')}
              </Button>
            ) : null}
            {!can || a.state === 'cancelled' || (a.state === 'booked' && !started) ? (
              <Button variant={a.state === 'booked' && !started && can ? 'secondary' : 'primary'} onClick={onClose}>
                {t('common.close')}
              </Button>
            ) : null}
          </>
        )
      }
    >
      {mode === 'move' ? (
        <MovePanel a={a} today={today} onCancel={() => setMode('view')} onDone={() => (setMode('view'), done())} />
      ) : mode === 'edit' ? (
        <EditContact a={a} onCancel={() => setMode('view')} onDone={() => (setMode('view'), done())} />
      ) : (
        <div className="ap-detail">
          <div className="ap-detail-head">
            <span className={`ap-detail-date tone-${a.service_tone}`} aria-hidden="true">
              <span>{new Intl.DateTimeFormat(currentLang(), { month: 'short', timeZone: 'UTC' }).format(new Date(`${a.day}T00:00:00Z`))}</span>
              <strong>{Number(a.day.slice(8))}</strong>
            </span>
            <div>
              <strong className="ap-detail-when">
                {longDay(a.day)}, {a.time}–{a.end_time}
              </strong>
              <span className="muted">{t('ap.with_name', { name: a.staff_name })}</span>
            </div>
            <StateBadge state={a.state} />
          </div>
          <dl className="ap-facts">
            <div>
              <dt>{t('ap.c_customer')}</dt>
              <dd>
                <strong>{a.name}</strong>
                {a.history.done || a.history.no_show ? (
                  <span className={`small ${a.history.no_show ? 'ap-warn' : 'muted'}`}>
                    {' · '}
                    {tn('ap.hist_done', a.history.done)}
                    {a.history.no_show ? `, ${tn('ap.hist_no_show', a.history.no_show)}` : ''}
                  </span>
                ) : (
                  <span className="muted small"> · {t('ap.first_visit')}</span>
                )}
              </dd>
            </div>
            <div>
              <dt>{t('ap.c_phone')}</dt>
              <dd>{a.phone ? <a href={`tel:${a.phone.replace(/\s/g, '')}`}>{a.phone}</a> : '—'}</dd>
            </div>
            <div>
              <dt>{t('ap.c_email')}</dt>
              <dd>{a.email ? <a href={`mailto:${a.email}`}>{a.email}</a> : '—'}</dd>
            </div>
            <div>
              <dt>{t('ap.c_price')}</dt>
              <dd>{a.price ? money(a.price) : t('ap.free')}</dd>
            </div>
            <div>
              <dt>{t('ap.c_source')}</dt>
              <dd>
                {t(`ap.src_${a.source}` as MessageKey)} · {dateTime(a.created_at)}
              </dd>
            </div>
            {a.note ? (
              <div className="ap-fact-wide">
                <dt>{t('ap.note')}</dt>
                <dd className="ap-note">{a.note}</dd>
              </div>
            ) : null}
            {a.state === 'cancelled' ? (
              <div className="ap-fact-wide">
                <dt>{t('ap.cancelled')}</dt>
                <dd>
                  {a.cancelled_at ? dateTime(a.cancelled_at) : ''}
                  {a.cancel_reason ? ` · ${a.cancel_reason}` : ''}
                </dd>
              </div>
            ) : null}
          </dl>
          {can && a.state !== 'cancelled' ? (
            <div className="ap-detail-tools">
              <Button size="sm" variant="ghost" icon="edit" onClick={() => setMode('edit')}>
                {t('ap.edit_contact')}
              </Button>
              {a.state === 'booked' ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon="link"
                  onClick={() => void navigator.clipboard?.writeText(`${window.location.origin}/book/${slug}/visit/${a.token}`).then(() => toast(t('ap.link_copied')))}
                >
                  {t('ap.copy_link')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {cancelling ? (
        <Confirm
          title={t('ap.cancel_q')}
          body={
            <div className="form-stack">
              <p>{t('ap.cancel_body', { name: a.name, when: `${longDay(a.day)}, ${a.time}` })}</p>
              <Field label={t('ap.reason')} hint={t('ap.reason_hint')}>
                <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
              </Field>
            </div>
          }
          confirm={t('ap.cancel_appt')}
          danger
          busy={busy}
          onClose={() => setCancelling(false)}
          onConfirm={() =>
            void run(
              () =>
                action('appointments.cancel', { id, reason: reason.trim() || null }).then(() => {
                  setCancelling(false);
                  done();
                }),
              t('ap.saved_cancelled'),
            )
          }
        />
      ) : null}
    </Dialog>
  );
}

/** A day and its free times for a service, at the desk. */
function SlotPicker({
  service,
  staff,
  day,
  setDay,
  today,
  except,
  value,
  onChange,
}: {
  service: string | null;
  staff: string | null;
  day: string;
  setDay: (d: string) => void;
  today: string;
  except?: string;
  value: Slot | null;
  onChange: (s: Slot | null) => void;
}) {
  const [slots, setSlots] = useState<Slot[] | null>(null);
  useEffect(() => {
    if (!service) return;
    let live = true;
    setSlots(null);
    action<Slot[]>('appointments.slots', { service, day, staff, except: except ?? null }).then(
      (s) => live && setSlots(s),
      () => live && setSlots([]),
    );
    return () => {
      live = false;
    };
  }, [service, staff, day, except]);
  return (
    <div className="ap-slots">
      <div className="ap-slots-day">
        <IconButton icon="back" label={t('ap.prev_day')} onClick={() => setDay(plusDays(day, -1))} disabled={day <= today} />
        <input type="date" className="input ap-date" value={day} min={today} onChange={(e) => e.target.value && setDay(e.target.value)} aria-label={t('ap.pick_day')} />
        <IconButton icon="chevronRight" label={t('ap.next_day')} onClick={() => setDay(plusDays(day, 1))} />
        <span className="muted small">{wdName(weekdayOf(day))}</span>
      </div>
      {!service ? (
        <p className="muted small">{t('ap.pick_service_first')}</p>
      ) : !slots ? (
        <Skeleton h={90} />
      ) : !slots.length ? (
        <p className="ap-slots-none">
          <Icon name="calendar" size={18} /> {t('ap.no_slots')}
        </p>
      ) : (
        <div className="ap-times" role="radiogroup" aria-label={t('ap.time')}>
          {slots.map((s) => (
            <button
              key={s.at}
              type="button"
              role="radio"
              aria-checked={value?.at === s.at}
              className={`ap-time${value?.at === s.at ? ' active' : ''}`}
              onClick={() => onChange(value?.at === s.at ? null : s)}
            >
              {s.time}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function useSetup() {
  const { data: team } = useAction<Team>('appointments.team');
  const { data: services } = useAction<Service[]>('appointments.services');
  return { team, services: services?.filter((s) => s.active) ?? null };
}

function BookDialog({ initial, today, onClose, onDone }: { initial: { staff?: string | null; day?: string; time?: string }; today: string; onClose: () => void; onDone: (id: string) => void }) {
  const { team, services } = useSetup();
  const [service, setService] = useState<string | null>(null);
  const [staff, setStaff] = useState<string | null>(initial.staff ?? null);
  const [day, setDay] = useState(initial.day && initial.day >= today ? initial.day : today);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const people = useMemo(() => (team?.staff ?? []).filter((p) => p.active && (!service || p.services.includes(service))), [team, service]);
  // A click on the calendar chose the person: offer only what they do, the first one picked.
  useEffect(() => {
    if (!services || service) return;
    const mine = initial.staff ? team?.staff.find((p) => p.id === initial.staff) : null;
    const first = services.find((s) => !mine || mine.services.includes(s.id));
    if (first) setService(first.id);
  }, [services, team, service, initial.staff]);
  // The time clicked on the calendar, once it shows among the free times.
  const wanted = useRef(initial.time ?? null);
  useEffect(() => {
    if (!service || !wanted.current) return;
    let live = true;
    action<Slot[]>('appointments.slots', { service, day, staff }).then((s) => {
      const hit = s.find((x) => x.time === wanted.current);
      if (live && hit) setSlot(hit);
      wanted.current = null;
    });
    return () => {
      live = false;
    };
  }, [service, day, staff]);
  const chosen = services?.find((s) => s.id === service) ?? null;
  const reach = !!phone.trim() || !!email.trim();
  const ok = !!service && !!slot && name.trim().length > 0 && reach;
  const save = () => {
    setTried(true);
    if (!ok || !slot) return;
    void run(
      () =>
        action<{ id: string }>('appointments.book', {
          service,
          staff: staff ?? (slot.staff.length === 1 ? slot.staff[0] : null),
          starts: `${day}T${slot.time}`,
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          note: note.trim() || null,
        }).then((r) => onDone(r.id)),
      t('ap.booked'),
    );
  };
  return (
    <Dialog
      title={t('ap.new')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="calendar" busy={busy} onClick={save}>
            {slot ? t('ap.book_at', { time: slot.time, day: dayText(day) }) : t('ap.book')}
          </Button>
        </>
      }
    >
      {!team || !services ? (
        <Skeleton h={320} />
      ) : (
        <div className="ap-book">
          <div className="ap-book-pick">
            <div className="ap-two">
              <Field label={t('ap.c_service')} required>
                <select
                  className="input"
                  value={service ?? ''}
                  onChange={(e) => {
                    setService(e.target.value || null);
                    setSlot(null);
                    const p = team.staff.find((x) => x.id === staff);
                    if (p && !p.services.includes(e.target.value)) setStaff(null);
                  }}
                >
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {minutesText(s.duration)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('ap.c_with')}>
                <select
                  className="input"
                  value={staff ?? ''}
                  onChange={(e) => {
                    setStaff(e.target.value || null);
                    setSlot(null);
                  }}
                >
                  <option value="">{t('ap.anyone')}</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            {chosen ? (
              <p className="muted small ap-book-svc">
                {minutesText(chosen.duration)}
                {chosen.buffer ? ` + ${t('ap.buffer_n', { n: chosen.buffer })}` : ''} · {chosen.price ? money(chosen.price) : t('ap.free')}
              </p>
            ) : null}
            <SlotPicker
              service={service}
              staff={staff}
              day={day}
              setDay={(d) => {
                setDay(d);
                setSlot(null);
              }}
              today={today}
              value={slot}
              onChange={setSlot}
            />
            {tried && !slot ? (
              <p className="ap-error" role="alert">
                {t('ap.need_time')}
              </p>
            ) : null}
            {slot && !staff && slot.staff.length > 1 ? <p className="muted small">{t('ap.anyone_note')}</p> : null}
          </div>
          <div className="ap-book-who form-stack">
            <Field label={t('ap.c_customer')} required error={tried && !name.trim() ? t('ap.need_name') : undefined}>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoComplete="off" />
            </Field>
            <Field label={t('ap.c_phone')} error={tried && !reach ? t('ap.need_reach') : undefined}>
              <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} autoComplete="off" />
            </Field>
            <Field label={t('ap.c_email')}>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={160} autoComplete="off" />
            </Field>
            <Field label={t('ap.note')} counter={`${note.length}/600`}>
              <textarea className="input textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={600} />
            </Field>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function MovePanel({ a, today, onCancel, onDone }: { a: Detail; today: string; onCancel: () => void; onDone: () => void }) {
  const { team } = useSetup();
  const [staff, setStaff] = useState<string | null>(a.staff);
  const [day, setDay] = useState(a.day >= today ? a.day : today);
  const [slot, setSlot] = useState<Slot | null>(null);
  const { busy, run } = useSave();
  const people = (team?.staff ?? []).filter((p) => p.active && p.services.includes(a.service));
  const save = () => slot && void run(() => action('appointments.move', { id: a.id, staff: staff ?? slot.staff[0], starts: `${day}T${slot.time}` }).then(onDone), t('ap.moved'));
  return (
    <div className="form-stack">
      <p className="muted">{t('ap.move_from', { when: `${longDay(a.day)}, ${a.time}`, name: a.staff_name })}</p>
      <Field label={t('ap.c_with')}>
        <select
          className="input"
          value={staff ?? ''}
          onChange={(e) => {
            setStaff(e.target.value || null);
            setSlot(null);
          }}
        >
          <option value="">{t('ap.anyone')}</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>
      <SlotPicker
        service={a.service}
        staff={staff}
        day={day}
        setDay={(d) => {
          setDay(d);
          setSlot(null);
        }}
        today={today}
        except={a.id}
        value={slot}
        onChange={setSlot}
      />
      <div className="ap-inline-actions">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.back')}
        </Button>
        <Button variant="primary" icon="move" disabled={!slot} busy={busy} onClick={save}>
          {slot ? t('ap.move_to', { time: slot.time, day: dayText(day) }) : t('ap.move')}
        </Button>
      </div>
    </div>
  );
}

function EditContact({ a, onCancel, onDone }: { a: Detail; onCancel: () => void; onDone: () => void }) {
  const [name, setName] = useState(a.name);
  const [phone, setPhone] = useState(a.phone ?? '');
  const [email, setEmail] = useState(a.email ?? '');
  const [note, setNote] = useState(a.note ?? '');
  const { busy, run } = useSave();
  return (
    <div className="form-stack">
      <Field label={t('ap.c_customer')} required>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
      </Field>
      <div className="ap-two">
        <Field label={t('ap.c_phone')}>
          <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} />
        </Field>
        <Field label={t('ap.c_email')}>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={160} />
        </Field>
      </div>
      <Field label={t('ap.note')} counter={`${note.length}/600`}>
        <textarea className="input textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={600} />
      </Field>
      <div className="ap-inline-actions">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.back')}
        </Button>
        <Button
          variant="primary"
          icon="check"
          busy={busy}
          disabled={!name.trim()}
          onClick={() =>
            void run(
              () => action('appointments.update', { id: a.id, name: name.trim(), phone: phone.trim() || null, email: email.trim() || null, note: note.trim() || null }).then(onDone),
              t('ap.saved'),
            )
          }
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

// ---- services -----------------------------------------------------------------------------

function ServicesTab({ can, onChange }: { can: { manage: boolean }; onChange: () => void }) {
  const { data: rows, reload } = useAction<Service[]>('appointments.services');
  const [edit, setEdit] = useState<Service | 'new' | null>(null);
  const [removing, setRemoving] = useState<Service | null>(null);
  const { busy, run } = useSave();
  const changed = () => {
    void reload();
    onChange();
  };
  return (
    <section className="panel ap-services">
      <div className="panel-head">
        <div>
          <h2>{t('ap.services_h')}</h2>
          <p className="muted small">{t('ap.services_hint')}</p>
        </div>
        {can.manage ? (
          <Button icon="plus" onClick={() => setEdit('new')}>
            {t('ap.add_service')}
          </Button>
        ) : null}
      </div>
      {!rows ? (
        <Skeleton h={200} />
      ) : !rows.length ? (
        <EmptyState icon="sparkle" title={t('ap.no_services')} body={t('ap.no_services_body')} />
      ) : (
        <ul className="ap-svc-list">
          {rows.map((s) => (
            <li key={s.id} className={`ap-svc${s.active ? '' : ' off'}`}>
              <span className={`ap-svc-bar tone-${s.tone}`} aria-hidden="true" />
              <div className="ap-svc-main">
                <strong>
                  {s.name}
                  {!s.active ? <span className="evp-chip muted">{t('ap.archived')}</span> : !s.online ? <span className="evp-chip muted">{t('ap.phone_only')}</span> : null}
                </strong>
                {s.description ? <span className="muted small ap-svc-desc">{s.description}</span> : null}
                <span className="ap-svc-meta small">
                  <span>
                    <Icon name="clock" size={14} /> {minutesText(s.duration)}
                    {s.buffer ? ` + ${t('ap.buffer_n', { n: s.buffer })}` : ''}
                  </span>
                  <span>
                    <Icon name="users" size={14} /> {s.staff.length ? s.staff.map((p) => p.name).join(', ') : <span className="ap-warn">{t('ap.nobody')}</span>}
                  </span>
                  <span>
                    <Icon name="calendar" size={14} /> {tn('ap.n_booked', s.booked)}
                  </span>
                </span>
              </div>
              <strong className="ap-svc-price">{s.price ? money(s.price) : t('ap.free')}</strong>
              {can.manage ? (
                <div className="ap-svc-tools">
                  <IconButton icon="edit" label={t('ap.edit_named', { name: s.name })} onClick={() => setEdit(s)} />
                  {s.active ? <IconButton icon="trash" label={t('ap.remove_named', { name: s.name })} onClick={() => setRemoving(s)} /> : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {edit ? (
        <ServiceDialog
          initial={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            changed();
          }}
        />
      ) : null}
      {removing ? (
        <Confirm
          title={t('ap.remove_q', { name: removing.name })}
          body={removing.booked ? t('ap.remove_archive') : t('ap.remove_body')}
          confirm={removing.booked ? t('ap.archive') : t('ap.remove')}
          danger
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={() =>
            void run(
              () =>
                action('appointments.remove_service', { id: removing.id }).then(() => {
                  setRemoving(null);
                  changed();
                }),
              removing.booked ? t('ap.archived_done') : t('ap.removed'),
            )
          }
        />
      ) : null}
    </section>
  );
}

function ToneField({ value, onChange }: { value: ToneName; onChange: (t: ToneName) => void }) {
  return (
    <fieldset className="ap-tones">
      <legend className="field-label">{t('ap.colour')}</legend>
      {TONES.map((x) => (
        <button
          key={x}
          type="button"
          className={`ap-tone tone-${x}${value === x ? ' active' : ''}`}
          aria-pressed={value === x}
          aria-label={t(`ap.tone_${x}` as MessageKey)}
          onClick={() => onChange(x)}
        >
          {value === x ? <Icon name="check" size={14} /> : null}
        </button>
      ))}
    </fieldset>
  );
}

function ServiceDialog({ initial, onClose, onSaved }: { initial: Service | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [duration, setDuration] = useState(String(initial?.duration ?? 30));
  const [buffer, setBuffer] = useState(String(initial?.buffer ?? 0));
  const [price, setPrice] = useState(initial ? (initial.price / 100).toString() : '');
  const [online, setOnline] = useState(initial?.online ?? true);
  const [active, setActive] = useState(initial?.active ?? true);
  const [tone, setTone] = useState<ToneName>(initial?.tone ?? 'blue');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const d = Number(duration);
  const b = Number(buffer);
  const p = price.trim() ? parseMoney(price) : 0;
  const dOk = Number.isInteger(d) && d >= 5 && d <= 720;
  const bOk = Number.isInteger(b) && b >= 0 && b <= 240;
  const ok = name.trim() && dOk && bOk && p !== null;
  const save = () => {
    setTried(true);
    if (!ok) return;
    void run(
      () =>
        action('appointments.save_service', { id: initial?.id ?? null, name: name.trim(), description: description.trim() || null, duration: d, buffer: b, price: p, active, online, tone }).then(
          onSaved,
        ),
      t('ap.saved'),
    );
  };
  return (
    <Dialog
      title={initial ? t('ap.edit_service') : t('ap.add_service')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('ap.s_name')} required error={tried && !name.trim() ? t('ap.need_service_name') : undefined}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder={t('ap.s_name_ph')} />
        </Field>
        <Field label={t('ap.s_desc')} hint={t('ap.s_desc_hint')} counter={`${description.length}/600`}>
          <textarea className="input textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={600} />
        </Field>
        <div className="ap-three">
          <Field label={t('ap.s_duration')} hint={t('ap.in_minutes')} error={tried && !dOk ? t('ap.need_duration') : undefined}>
            <input className="input" inputMode="numeric" value={duration} onChange={(e) => setDuration(e.target.value.replace(/\D/g, ''))} />
          </Field>
          <Field label={t('ap.s_buffer')} hint={t('ap.s_buffer_hint')} error={tried && !bOk ? t('ap.need_buffer') : undefined}>
            <input className="input" inputMode="numeric" value={buffer} onChange={(e) => setBuffer(e.target.value.replace(/\D/g, ''))} />
          </Field>
          <Field label={t('ap.s_price')} hint={t('ap.s_price_hint')} error={tried && p === null ? t('ap.need_price') : undefined}>
            <input className="input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" />
          </Field>
        </div>
        <ToneField value={tone} onChange={setTone} />
        <div className="setting-row">
          <div>
            <strong>{t('ap.s_online')}</strong>
            <p className="muted">{t('ap.s_online_hint')}</p>
          </div>
          <Switch checked={online} onChange={setOnline} label={t('ap.s_online')} />
        </div>
        {initial ? (
          <div className="setting-row">
            <div>
              <strong>{t('ap.s_active')}</strong>
              <p className="muted">{t('ap.s_active_hint')}</p>
            </div>
            <Switch checked={active} onChange={setActive} label={t('ap.s_active')} />
          </div>
        ) : (
          <p className="muted small">{t('ap.s_new_note')}</p>
        )}
      </div>
    </Dialog>
  );
}

// ---- the team -----------------------------------------------------------------------------

const hoursText = (hours: { weekday: number; starts: string; ends: string }[]) => {
  // Days with the same hours are written together: "Mon–Fri 09:00–17:00".
  const byDay = new Map<number, string>();
  for (const h of [...hours].sort((a, b) => mins(a.starts) - mins(b.starts))) byDay.set(h.weekday, [byDay.get(h.weekday), `${h.starts}–${h.ends}`].filter(Boolean).join(', '));
  const out: string[] = [];
  let run: number[] = [];
  const flush = () => {
    if (!run.length) return;
    const label = run.length > 2 ? `${wdName(run[0]!, 'short')}–${wdName(run.at(-1)!, 'short')}` : run.map((w) => wdName(w, 'short')).join(', ');
    out.push(`${label} ${byDay.get(run[0]!)}`);
    run = [];
  };
  for (let w = 1; w <= 7; w++) {
    const v = byDay.get(w);
    if (v && run.length && byDay.get(run[0]!) === v && run.at(-1) === w - 1) run.push(w);
    else {
      flush();
      if (v) run = [w];
    }
  }
  flush();
  return out;
};

/** "5 Nov – 6 Nov 2026" for whole days, else the two moments. */
const offText = (o: { starts_local: string; ends_local: string }) => {
  const whole = o.starts_local.endsWith('T00:00') && o.ends_local.endsWith('T23:59');
  if (!whole) return `${dateTime(o.starts_local)} – ${o.starts_local.slice(0, 10) === o.ends_local.slice(0, 10) ? o.ends_local.slice(11) : dateTime(o.ends_local)}`;
  const a = o.starts_local.slice(0, 10);
  const b = o.ends_local.slice(0, 10);
  return a === b ? dayText(a) : `${dayText(a)} – ${dayText(b)}`;
};

function TeamTab({ can, onChange }: { can: { manage: boolean }; onChange: () => void }) {
  const { data, reload } = useAction<Team>('appointments.team');
  const [edit, setEdit] = useState<Person | 'new' | null>(null);
  const [off, setOff] = useState<Person | null>(null);
  const [removeOff, setRemoveOff] = useState<string | null>(null);
  const { busy, run } = useSave();
  const changed = () => {
    void reload();
    onChange();
  };
  return (
    <section className="ap-team">
      <div className="panel-head ap-team-head">
        <div>
          <h2>{t('ap.team_h')}</h2>
          <p className="muted small">{t('ap.team_hint')}</p>
        </div>
        {can.manage ? (
          <Button icon="plus" onClick={() => setEdit('new')} disabled={!data?.services.length}>
            {t('ap.add_person')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : !data.staff.length ? (
        <div className="panel">
          <EmptyState icon="users" title={t('ap.no_team')} body={data.services.length ? t('ap.no_team_body') : t('ap.services_first')} />
        </div>
      ) : (
        <div className="ap-people">
          {data.staff.map((p) => {
            const lines = hoursText(p.hours);
            return (
              <article key={p.id} className={`panel ap-person${p.active ? '' : ' off'}`}>
                <header className="ap-person-head">
                  <span className={`ap-avatar lg tone-${p.tone}`} aria-hidden="true">
                    {initials(p.name)}
                  </span>
                  <div className="ap-person-name">
                    <strong>{p.name}</strong>
                    <span className="muted small">{p.active ? tn('ap.n_upcoming', p.upcoming) : t('ap.not_bookable')}</span>
                  </div>
                  {can.manage ? <IconButton icon="edit" label={t('ap.edit_named', { name: p.name })} onClick={() => setEdit(p)} /> : null}
                </header>
                <div className="ap-person-sec">
                  <span className="field-label">{t('ap.working_hours')}</span>
                  {lines.length ? (
                    <ul className="ap-hours-list">
                      {lines.map((l) => (
                        <li key={l}>{l}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="ap-warn small">
                      <Icon name="alert" size={14} /> {t('ap.no_hours')}
                    </p>
                  )}
                </div>
                <div className="ap-person-sec">
                  <span className="field-label">{t('ap.does')}</span>
                  <div className="ap-chips">
                    {p.services.length ? (
                      data.services
                        .filter((s) => p.services.includes(s.id))
                        .map((s) => (
                          <span key={s.id} className="ap-chip">
                            {s.name}
                          </span>
                        ))
                    ) : (
                      <span className="ap-warn small">{t('ap.no_services_person')}</span>
                    )}
                  </div>
                </div>
                <div className="ap-person-sec">
                  <span className="field-label ap-off-head">
                    {t('ap.time_off')}
                    {can.manage ? (
                      <button type="button" className="link-button" onClick={() => setOff(p)}>
                        {t('ap.add_time_off')}
                      </button>
                    ) : null}
                  </span>
                  {p.off.length ? (
                    <ul className="ap-off-list">
                      {p.off.map((o) => (
                        <li key={o.id}>
                          <Icon name="moon" size={14} />
                          <span>
                            {offText(o)}
                            {o.reason ? <span className="muted"> · {o.reason}</span> : null}
                          </span>
                          {can.manage ? <IconButton icon="x" label={t('ap.remove_time_off')} onClick={() => setRemoveOff(o.id)} /> : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="muted small">{t('ap.no_time_off')}</p>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
      {edit && data ? (
        <PersonDialog
          initial={edit === 'new' ? null : edit}
          team={data}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            changed();
          }}
        />
      ) : null}
      {off ? (
        <TimeOffDialog
          person={off}
          onClose={() => setOff(null)}
          onSaved={() => {
            setOff(null);
            changed();
          }}
        />
      ) : null}
      {removeOff ? (
        <Confirm
          title={t('ap.remove_time_off')}
          body={t('ap.remove_time_off_body')}
          confirm={t('ap.remove')}
          busy={busy}
          onClose={() => setRemoveOff(null)}
          onConfirm={() =>
            void run(
              () =>
                action('appointments.remove_timeoff', { id: removeOff }).then(() => {
                  setRemoveOff(null);
                  changed();
                }),
              t('ap.removed'),
            )
          }
        />
      ) : null}
    </section>
  );
}

type DayHours = { on: boolean; spans: { starts: string; ends: string }[] };

function PersonDialog({ initial, team, onClose, onSaved }: { initial: Person | null; team: Team; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [user, setUser] = useState(initial?.user ?? '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [tone, setTone] = useState<ToneName>(initial?.tone ?? TONES[team.staff.length % TONES.length]!);
  const [services, setServices] = useState<string[]>(initial?.services ?? team.services.map((s) => s.id));
  const [week, setWeek] = useState<DayHours[]>(() =>
    Array.from({ length: 7 }, (_, i) => {
      const spans = (initial?.hours ?? []).filter((h) => h.weekday === i + 1).map((h) => ({ starts: h.starts, ends: h.ends }));
      if (initial) return { on: spans.length > 0, spans: spans.length ? spans : [{ starts: '09:00', ends: '17:00' }] };
      return { on: i < 5, spans: [{ starts: '09:00', ends: '17:00' }] };
    }),
  );
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const setDay = (i: number, d: DayHours) => setWeek(week.map((x, n) => (n === i ? d : x)));
  const dayError = (d: DayHours) => {
    if (!d.on) return null;
    const s = [...d.spans].sort((a, b) => mins(a.starts) - mins(b.starts));
    if (s.some((x) => !/^\d{2}:\d{2}$/.test(x.starts) || !/^\d{2}:\d{2}$/.test(x.ends) || mins(x.ends) <= mins(x.starts))) return t('ap.hours_order');
    for (let i = 1; i < s.length; i++) if (mins(s[i]!.starts) < mins(s[i - 1]!.ends)) return t('ap.hours_overlap');
    return null;
  };
  const errors = week.map(dayError);
  const ok = name.trim() && !errors.some(Boolean);
  const copyDown = (i: number) => setWeek(week.map((x, n) => (n > i && x.on ? { on: true, spans: week[i]!.spans.map((s) => ({ ...s })) } : x)));
  const save = () => {
    setTried(true);
    if (!ok) return;
    const hours = week.flatMap((d, i) => (d.on ? d.spans.map((s) => ({ weekday: i + 1, starts: s.starts, ends: s.ends })) : []));
    void run(() => action('appointments.save_staff', { id: initial?.id ?? null, name: name.trim(), user: user || null, active, services, tone, hours }).then(onSaved), t('ap.saved'));
  };
  return (
    <Dialog
      title={initial ? t('ap.edit_person') : t('ap.add_person')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="ap-two">
          <Field label={t('ap.p_name')} required error={tried && !name.trim() ? t('ap.need_person_name') : undefined}>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
          </Field>
          <Field label={t('ap.p_user')} hint={t('ap.p_user_hint')}>
            <select className="input" value={user} onChange={(e) => setUser(e.target.value)}>
              <option value="">{t('ap.p_no_user')}</option>
              {team.users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <ToneField value={tone} onChange={setTone} />
        <fieldset className="ap-svc-picks">
          <legend className="field-label">{t('ap.does')}</legend>
          {team.services.map((s) => (
            <label key={s.id} className="ap-check">
              <input type="checkbox" checked={services.includes(s.id)} onChange={(e) => setServices(e.target.checked ? [...services, s.id] : services.filter((x) => x !== s.id))} />
              <span>{s.name}</span>
            </label>
          ))}
        </fieldset>
        <fieldset className="ap-week">
          <legend className="field-label">{t('ap.working_hours')}</legend>
          {week.map((d, i) => (
            <div key={i} className={`ap-week-day${d.on ? '' : ' off'}`}>
              <div className="ap-week-name">
                <Switch checked={d.on} onChange={(on) => setDay(i, { ...d, on })} label={wdName(i + 1)} />
                <span>{wdName(i + 1)}</span>
              </div>
              {d.on ? (
                <div className="ap-week-spans">
                  {d.spans.map((s, k) => (
                    <div key={k} className="ap-span">
                      <input
                        className="input"
                        type="time"
                        value={s.starts}
                        aria-label={t('ap.from_on', { day: wdName(i + 1) })}
                        onChange={(e) => setDay(i, { ...d, spans: d.spans.map((x, n) => (n === k ? { ...x, starts: e.target.value } : x)) })}
                      />
                      <span aria-hidden="true">–</span>
                      <input
                        className="input"
                        type="time"
                        value={s.ends}
                        aria-label={t('ap.to_on', { day: wdName(i + 1) })}
                        onChange={(e) => setDay(i, { ...d, spans: d.spans.map((x, n) => (n === k ? { ...x, ends: e.target.value } : x)) })}
                      />
                      {d.spans.length > 1 ? <IconButton icon="x" label={t('ap.remove_span')} onClick={() => setDay(i, { ...d, spans: d.spans.filter((_, n) => n !== k) })} /> : null}
                    </div>
                  ))}
                  <div className="ap-span-tools">
                    {d.spans.length < 4 ? (
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => {
                          const lastEnd = d.spans.at(-1)?.ends ?? '13:00';
                          const start = Math.min(mins(lastEnd) + 60, 22 * 60);
                          setDay(i, { ...d, spans: [...d.spans, { starts: clockOf(start), ends: clockOf(Math.min(start + 180, 23 * 60 + 59)) }] });
                        }}
                      >
                        {t('ap.add_span')}
                      </button>
                    ) : null}
                    {i < 6 ? (
                      <button type="button" className="link-button" onClick={() => copyDown(i)}>
                        {t('ap.copy_down')}
                      </button>
                    ) : null}
                  </div>
                  {tried && errors[i] ? (
                    <p className="ap-error" role="alert">
                      {errors[i]}
                    </p>
                  ) : null}
                </div>
              ) : (
                <span className="muted small">{t('ap.day_off')}</span>
              )}
            </div>
          ))}
        </fieldset>
        {initial ? (
          <div className="setting-row">
            <div>
              <strong>{t('ap.p_active')}</strong>
              <p className="muted">{t('ap.p_active_hint')}</p>
            </div>
            <Switch checked={active} onChange={setActive} label={t('ap.p_active')} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

function TimeOffDialog({ person, onClose, onSaved }: { person: Person; onClose: () => void; onSaved: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [allDay, setAllDay] = useState(true);
  const [fromTime, setFromTime] = useState('09:00');
  const [toTime, setToTime] = useState('13:00');
  const [reason, setReason] = useState('');
  const { busy, run } = useSave();
  const { toast } = useUi();
  const starts = `${from}T${allDay ? '00:00' : fromTime}`;
  const ends = `${allDay ? to : from}T${allDay ? '23:59' : toTime}`;
  const ok = ends > starts;
  return (
    <Dialog
      title={t('ap.time_off_for', { name: person.name })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!ok}
            onClick={() =>
              void run(() =>
                action<{ clashes: number }>('appointments.add_timeoff', { staff: person.id, starts, ends, reason: reason.trim() || null }).then((r) => {
                  toast(r.clashes ? tn('ap.off_clashes', r.clashes) : t('ap.saved'));
                  onSaved();
                }),
              )
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="setting-row">
          <div>
            <strong>{t('ap.all_day')}</strong>
            <p className="muted">{t('ap.all_day_hint')}</p>
          </div>
          <Switch checked={allDay} onChange={setAllDay} label={t('ap.all_day')} />
        </div>
        {allDay ? (
          <div className="ap-two">
            <Field label={t('ap.first_day')}>
              <input
                className="input"
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  if (to < e.target.value) setTo(e.target.value);
                }}
              />
            </Field>
            <Field label={t('ap.last_day')} error={!ok ? t('ap.off_order') : undefined}>
              <input className="input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
        ) : (
          <div className="ap-three">
            <Field label={t('ap.c_day')}>
              <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label={t('ap.from')}>
              <input className="input" type="time" value={fromTime} onChange={(e) => setFromTime(e.target.value)} />
            </Field>
            <Field label={t('ap.to')} error={!ok ? t('ap.off_order') : undefined}>
              <input className="input" type="time" value={toTime} onChange={(e) => setToTime(e.target.value)} />
            </Field>
          </div>
        )}
        <Field label={t('ap.reason')} hint={t('ap.off_reason_hint')}>
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={120} placeholder={t('ap.off_reason_ph')} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- settings -----------------------------------------------------------------------------

function SettingsDialog({ initial, slug, onClose, onSaved }: { initial: Settings; slug: string; onClose: () => void; onSaved: () => void }) {
  const [s, setS] = useState<Settings>(initial);
  const [intro, setIntro] = useState(initial.intro ?? '');
  const { busy, run } = useSave();
  const { toast } = useUi();
  const num = (k: 'notice_hours' | 'days_ahead' | 'cancel_hours', v: string) => setS({ ...s, [k]: Number(v.replace(/\D/g, '') || 0) });
  const ok = s.days_ahead >= 1 && s.days_ahead <= 365 && s.notice_hours <= 168 && s.cancel_hours <= 168;
  return (
    <Dialog
      title={t('ap.settings')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!ok}
            onClick={() => void run(() => action('appointments.save_settings', { ...s, intro: intro.trim() || null }).then(onSaved), t('ap.saved'))}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="setting-row">
          <div>
            <strong>{t('ap.set_public')}</strong>
            <p className="muted">{t('ap.set_public_hint')}</p>
          </div>
          <Switch checked={s.public} onChange={(v) => setS({ ...s, public: v })} label={t('ap.set_public')} />
        </div>
        {s.public ? (
          <div className="ap-url">
            <span className="mono small">{publicUrl(slug)}</span>
            <IconButton icon="copy" label={t('ap.copy_url')} onClick={() => void navigator.clipboard?.writeText(publicUrl(slug)).then(() => toast(t('ap.link_copied')))} />
          </div>
        ) : null}
        <Field label={t('ap.set_intro')} hint={t('ap.set_intro_hint')} counter={`${intro.length}/600`}>
          <textarea className="input textarea" rows={3} value={intro} onChange={(e) => setIntro(e.target.value)} maxLength={600} />
        </Field>
        <div className="ap-two">
          <Field label={t('ap.set_step')} hint={t('ap.set_step_hint')}>
            <select className="input" value={s.step} onChange={(e) => setS({ ...s, step: Number(e.target.value) })}>
              {[5, 10, 15, 20, 30, 60].map((n) => (
                <option key={n} value={n}>
                  {t('ap.every_min', { n })}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('ap.set_notice')} hint={t('ap.set_notice_hint')} error={s.notice_hours > 168 ? t('ap.max_168') : undefined}>
            <input className="input" inputMode="numeric" value={String(s.notice_hours)} onChange={(e) => num('notice_hours', e.target.value)} />
          </Field>
          <Field label={t('ap.set_ahead')} hint={t('ap.set_ahead_hint')} error={s.days_ahead < 1 || s.days_ahead > 365 ? t('ap.ahead_range') : undefined}>
            <input className="input" inputMode="numeric" value={String(s.days_ahead)} onChange={(e) => num('days_ahead', e.target.value)} />
          </Field>
          <Field label={t('ap.set_cancel')} hint={t('ap.set_cancel_hint')} error={s.cancel_hours > 168 ? t('ap.max_168') : undefined}>
            <input className="input" inputMode="numeric" value={String(s.cancel_hours)} onChange={(e) => num('cancel_hours', e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
